import { get } from 'lodash';

import queryClient from 'common/utils/QueryClient';
import { useMutation } from 'common/hooks';
import { UseMutationOptions } from 'common/types/Api.type';
import { useEventEmitter, useSession } from 'common/hooks';
import { SkillEndorsementEventData } from 'common/types/Event-Data.type';
import { updateEndorsedSkillApi } from '../profiles.service';
import {
  EndorsedSkillResponse,
  MySkillEndorsementResponse,
  SkillEndorsementsResponse,
} from 'features/skills';
import { updateEndorsedSkill } from '../helpers/endorsed-skills';

export const useUpdateEndorsedSkill = (
  profileId: string,
  skillId: string,
  options: UseMutationOptions = {},
) => {
  const { account: user } = useSession();
  const { emit } = useEventEmitter<SkillEndorsementEventData>('SkillEndorsement');

  return useMutation(updateEndorsedSkillApi, {
    showSuccessSnackbar: true,
    transformInput: (input: any) => ({ profileId, skillId, rating: get(input, 'rating') }),
    onSuccess: async (res: EndorsedSkillResponse) => {
      const detailKey = ['profile', 'skill-endorsements', profileId, skillId];
      const listKey = ['profile', 'skill-endorsements', profileId];
      const myEndorsementKey = ['my-skill-endorsement', profileId, skillId];

      queryClient.setQueryData<MySkillEndorsementResponse>(
        myEndorsementKey,
        (current) => ({
          ...current,
          data: {
            ...(current?.data || {}),
            endorsement: {
              id: current?.data?.endorsement?.id || '',
              rating: Number(res.data?.loggedInUserRating ?? 0),
              endorsementDate:
                res.data?.loggedInUserEndorsementDate || new Date().toISOString(),
            },
          },
        }),
      );

      queryClient.setQueryData<SkillEndorsementsResponse>(detailKey, (current) => ({
        ...(current || {}),
        data: {
          ...(current?.data || {}),
          endorsedSkills: [res.data],
        },
      }));

      queryClient.setQueryData<SkillEndorsementsResponse>(listKey, (current) => {
        return {
          ...(current || {}),
          data: {
            ...(current?.data || {}),
            endorsedSkills: updateEndorsedSkill(
              current?.data?.endorsedSkills ?? [],
              skillId,
              res.data,
            ),
          },
        };
      });

      await Promise.all([
        queryClient.invalidateQueries(myEndorsementKey),
        queryClient.invalidateQueries(['skill-endorsers', profileId, skillId]),
      ]);

      emit({
        currentOrganization: user?.organizationId,
        profileId,
        skillId,
        data: res.data,
      });
    },
    ...options,
  });
};
